import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { versionApi, developerApi } from '../../services/api';
import {
  formatPercent, formatMs, formatNumber,
  getDeploymentConfig, getRiskScoreColor, formatVersion,
  getRiskStatusConfig,
} from '../../utils/formatters';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const COMPONENT_LABELS = {
  crash: 'Crash',
  anr: 'ANR',
  playback: 'Playback',
  startup: 'Startup',
  frameDrop: 'Frames',
  api: 'API',
};

const COMPONENT_COLORS = {
  crash: '#ef4444',
  anr: '#f59e0b',
  playback: '#6366f1',
  startup: '#06b6d4',
  frameDrop: '#a855f7',
  api: '#10b981',
};

const METRICS = [
  { key: 'crash_free_pct',     label: 'Crash-Free %',     fmt: formatPercent, higher: true },
  { key: 'crash_rate_per_10k', label: 'Crash Rate / 10k', fmt: formatNumber,  higher: false },
  { key: 'anr_rate_pct',       label: 'ANR Rate',         fmt: v => formatPercent(v, 3), higher: false },
  { key: 'cold_start_p90',     label: 'Startup P90',      fmt: formatMs,      higher: false },
  { key: 'cold_start_p50',     label: 'Startup P50',      fmt: formatMs,      higher: false },
  { key: 'total_sessions',     label: 'Sessions',         fmt: formatNumber,  higher: true },
  { key: 'total_users',        label: 'Users',            fmt: formatNumber,  higher: true },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getBarBg(score) {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-amber-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

function getScoreTextColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function scoreBorderColor(score) {
  if (score >= 90) return 'border-emerald-500';
  if (score >= 75) return 'border-amber-500';
  if (score >= 60) return 'border-orange-500';
  return 'border-red-500';
}

function riskLeftBorder(status) {
  const map = { stable: 'border-l-emerald-500', monitor: 'border-l-amber-500', risk: 'border-l-orange-500', critical: 'border-l-red-500' };
  return map[status] || 'border-l-gray-500';
}

function changeIndicator(v1, v2, higherIsBetter) {
  if (v1 == null || v2 == null) return { text: '-', color: 'text-gray-500', delta: 0 };
  const diff = v1 - v2;
  if (Math.abs(diff) < 0.001) return { text: '=', color: 'text-gray-400', delta: 0 };
  const better = higherIsBetter ? diff > 0 : diff < 0;
  return {
    text: better ? 'Improved' : 'Regressed',
    arrow: better ? '\u2191' : '\u2193',
    color: better ? 'text-emerald-400' : 'text-red-400',
    barColor: better ? 'bg-emerald-500' : 'bg-red-500',
    delta: diff,
  };
}

// ---------------------------------------------------------------------------
// AI Insight Generator (computed from data, no API call)
// ---------------------------------------------------------------------------
function generateInsight(risk1, risk2, vData1, vData2, v1Label, v2Label) {
  if (!risk1 || !risk2) return null;

  const s1 = risk1.releaseRiskScore ?? 0;
  const s2 = risk2.releaseRiskScore ?? 0;
  const scoreDiff = Math.abs(s1 - s2).toFixed(0);
  const winner = s1 >= s2 ? v1Label : v2Label;
  const loser  = s1 >= s2 ? v2Label : v1Label;
  const betterRisk = s1 >= s2 ? risk1 : risk2;

  // Build detail notes
  const notes = [];
  if (vData1 && vData2) {
    const cfDiff = ((vData1.crash_free_pct ?? 0) - (vData2.crash_free_pct ?? 0)).toFixed(2);
    if (Math.abs(cfDiff) > 0.01) {
      const cfBetter = cfDiff > 0 ? v1Label : v2Label;
      notes.push(`${cfBetter} has ${Math.abs(cfDiff)}% higher crash-free rate`);
    }
    const anrDiff = ((vData1.anr_rate_pct ?? 0) - (vData2.anr_rate_pct ?? 0)).toFixed(3);
    if (Math.abs(anrDiff) > 0.001) {
      const anrWorse = anrDiff > 0 ? v1Label : v2Label;
      notes.push(`${anrWorse} shows ${Math.abs(anrDiff)}% higher ANR rate`);
    }
    const startupDiff = (vData1.cold_start_p90 ?? 0) - (vData2.cold_start_p90 ?? 0);
    if (Math.abs(startupDiff) > 50) {
      const faster = startupDiff < 0 ? v1Label : v2Label;
      notes.push(`${faster} starts ${Math.abs(startupDiff).toFixed(0)}ms faster (P90)`);
    }
  }

  // Recommendation
  const bestScore = Math.max(s1, s2);
  let recommendation, recColor, recBg;
  if (bestScore >= 75) {
    recommendation = 'GO';
    recColor = 'text-emerald-400';
    recBg = 'bg-emerald-500/20';
  } else if (bestScore >= 50) {
    recommendation = 'HOLD';
    recColor = 'text-amber-400';
    recBg = 'bg-amber-500/20';
  } else {
    recommendation = 'ROLLBACK';
    recColor = 'text-red-400';
    recBg = 'bg-red-500/20';
  }

  const summary = notes.length > 0 ? notes.join('. ') + '.' : 'Both versions show comparable stability metrics.';

  return {
    winner,
    loser,
    scoreDiff,
    summary,
    recommendation,
    recColor,
    recBg,
    verdict: s1 === s2
      ? 'Both versions are equally scored.'
      : `${winner} is ${scoreDiff} points more stable than ${loser}.`,
  };
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function UnifiedVersionCompare() {
  const [searchParams] = useSearchParams();
  const urlV1 = searchParams.get('v1') || '';

  const [versions, setVersions] = useState([]);
  const [v1, setV1] = useState(urlV1);
  const [v2, setV2] = useState('');
  const [comparison, setComparison] = useState(null);
  const [riskCompare, setRiskCompare] = useState(null);
  const [loading, setLoading] = useState(false);

  // Version code lookup
  const versionCodeMap = useMemo(() => {
    const m = {};
    versions.forEach(v => { if (v.version_code) m[v.app_version] = v.version_code; });
    return m;
  }, [versions]);

  const v1Label = formatVersion(v1, versionCodeMap[v1]);
  const v2Label = formatVersion(v2, versionCodeMap[v2]);

  // Load version list
  useEffect(() => {
    versionApi.list().then(res => {
      const vList = res.data.versions || [];
      setVersions(vList);
      if (vList.length >= 2) {
        if (!v1) setV1(vList[0].app_version);
        setV2(prev => prev || vList[1]?.app_version || '');
      }
    }).catch(() => {});
  }, []);

  // Sync URL param
  useEffect(() => {
    if (urlV1 && urlV1 !== v1) setV1(urlV1);
  }, [urlV1]);

  // Auto-compare
  useEffect(() => {
    if (!v1 || !v2 || v1 === v2) return;
    setLoading(true);
    Promise.all([
      versionApi.compare(v1, v2),
      developerApi.getVersionRiskCompare(v1, v2).catch(() => ({ data: { versions: {} } })),
    ]).then(([compRes, riskRes]) => {
      setComparison(compRes.data);
      setRiskCompare(riskRes.data.versions || {});
    }).catch(() => {
      setComparison(null);
      setRiskCompare(null);
    }).finally(() => setLoading(false));
  }, [v1, v2]);

  // Derived data
  const vData1 = comparison?.versions?.find(v => v.app_version === v1);
  const vData2 = comparison?.versions?.find(v => v.app_version === v2);
  const risk1 = riskCompare?.[v1];
  const risk2 = riskCompare?.[v2];
  const insight = useMemo(
    () => generateInsight(risk1, risk2, vData1, vData2, v1Label, v2Label),
    [risk1, risk2, vData1, vData2, v1Label, v2Label],
  );

  const winnerVersion = risk1 && risk2
    ? (risk1.releaseRiskScore >= risk2.releaseRiskScore ? v1 : v2)
    : null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-5">

      {/* ---- 1. VERSION SELECTOR BAR ---- */}
      <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 p-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1 block">Version A</label>
          <select
            value={v1}
            onChange={e => setV1(e.target.value)}
            className="w-full bg-surface-700 border border-surface-600 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-brand-500/50"
          >
            <option value="">Select version...</option>
            {versions.map(v => (
              <option key={v.app_version} value={v.app_version}>
                {formatVersion(v.app_version, v.version_code)}
              </option>
            ))}
          </select>
        </div>

        <span className="text-gray-500 text-xl font-light select-none px-2">VS</span>

        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1 block">Version B</label>
          <select
            value={v2}
            onChange={e => setV2(e.target.value)}
            className="w-full bg-surface-700 border border-surface-600 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-brand-500/50"
          >
            <option value="">Select version...</option>
            {versions.map(v => (
              <option key={v.app_version} value={v.app_version}>
                {formatVersion(v.app_version, v.version_code)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="border-2 border-brand-500 border-t-transparent rounded-full w-8 h-8 animate-spin" />
          <span className="ml-3 text-sm text-gray-400">Comparing versions...</span>
        </div>
      )}

      {/* Main content when data is ready */}
      {!loading && comparison && vData1 && vData2 && (
        <>
          {/* ---- 2. AI DECISION SUMMARY ---- */}
          {insight && (
            <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="bg-brand-500/20 rounded-md p-2 flex-shrink-0">
                  <svg className="w-5 h-5 text-brand-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-200 mb-1">Which version is better?</h3>
                  <p className="text-base font-bold text-gray-100 mb-1">{insight.verdict}</p>
                  <p className="text-sm text-gray-400 mb-3">{insight.summary}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Recommendation</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${insight.recBg} ${insight.recColor}`}>
                      {insight.recommendation}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ---- 3. RISK COMPARISON CARDS ---- */}
          {(risk1 || risk2) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: v1Label, risk: risk1, ver: v1 },
                { label: v2Label, risk: risk2, ver: v2 },
              ].map(({ label, risk, ver }) => {
                if (!risk) return (
                  <div key={ver} className="bg-surface-700/40 rounded-xl border border-surface-600/50 p-4 opacity-60">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">{label}</p>
                    <p className="text-sm text-gray-500 mt-2">No risk data available</p>
                  </div>
                );

                const score = risk.releaseRiskScore ?? 0;
                const deployConfig = getDeploymentConfig(risk.deploymentRecommendation);
                const statusConfig = getRiskStatusConfig(risk.riskStatus);
                const cs = risk.componentScores || {};
                const isWinner = ver === winnerVersion;

                return (
                  <div
                    key={ver}
                    className={`bg-surface-700/40 rounded-xl border border-surface-600/50 border-l-4 ${riskLeftBorder(risk.riskStatus)} p-4 relative`}
                  >
                    {/* Winner badge */}
                    {isWinner && (
                      <div className="absolute top-3 right-3 text-emerald-400">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}

                    <div className="flex items-start gap-4">
                      {/* Score circle */}
                      <div className="flex flex-col items-center flex-shrink-0 gap-2">
                        <div className={`w-16 h-16 rounded-full border-[3px] flex items-center justify-center ${scoreBorderColor(score)}`}>
                          <span className={`text-xl font-bold font-mono ${getRiskScoreColor(score)}`}>
                            {Math.round(score)}
                          </span>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusConfig.bg} ${statusConfig.border} ${statusConfig.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot} ${risk.riskStatus === 'critical' ? 'animate-pulse' : ''}`} />
                          {statusConfig.label}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border mb-3 ${deployConfig.bg} ${deployConfig.color} ${deployConfig.border}`}>
                          {deployConfig.label}
                        </div>

                        {/* Component score bars */}
                        <div className="space-y-1.5">
                          {Object.entries(COMPONENT_LABELS).map(([key, clabel]) => {
                            const val = cs[key] ?? 0;
                            return (
                              <div key={key} className="flex items-center gap-1.5">
                                <span className="text-[10px] text-gray-500 w-[44px] flex-shrink-0">{clabel}</span>
                                <div className="flex-1 h-1.5 bg-surface-600 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${getBarBg(val)}`} style={{ width: `${Math.min(100, val)}%` }} />
                                </div>
                                <span className={`text-[10px] font-mono w-6 text-right ${getScoreTextColor(val)}`}>{Math.round(val)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Reasons */}
                    {risk.recommendationReasons?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-surface-600/50">
                        <ul className="space-y-0.5">
                          {risk.recommendationReasons.slice(0, 3).map((r, i) => (
                            <li key={i} className="text-[11px] text-gray-500 flex gap-1.5">
                              <span className="text-gray-600 flex-shrink-0">&#x2022;</span>{r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ---- 4. METRIC COMPARISON — DELTA BARS ---- */}
          <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-brand-500/20 rounded-md p-1.5">
                <svg className="w-4 h-4 text-brand-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-200">Metric Comparison</h3>
            </div>

            {/* Header row */}
            <div className="grid grid-cols-[120px_80px_1fr_80px_90px] gap-2 mb-2 px-1">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Metric</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold text-right">{v1Label}</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold text-center">Delta</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold text-right">{v2Label}</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold text-right">Status</span>
            </div>

            <div className="space-y-1">
              {METRICS.map(({ key, label, fmt, higher }) => {
                const val1 = vData1[key];
                const val2 = vData2[key];
                const ci = changeIndicator(val1, val2, higher);

                // Compute bar width (normalized delta)
                let barPct = 0;
                if (val1 != null && val2 != null && (val1 + val2) > 0) {
                  barPct = Math.min(100, (Math.abs(ci.delta) / Math.max(Math.abs(val1), Math.abs(val2), 1)) * 100);
                }

                return (
                  <div
                    key={key}
                    className="grid grid-cols-[120px_80px_1fr_80px_90px] gap-2 items-center px-1 py-2 rounded-lg hover:bg-surface-700/40 transition-colors"
                  >
                    <span className="text-xs text-gray-400 font-medium">{label}</span>
                    <span className="text-xs font-mono text-gray-200 text-right">
                      {val1 != null ? fmt(val1) : 'N/A'}
                    </span>

                    {/* Delta bar */}
                    <div className="h-2 bg-surface-600/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${ci.barColor || 'bg-gray-600'}`}
                        style={{ width: `${Math.max(2, barPct)}%` }}
                      />
                    </div>

                    <span className="text-xs font-mono text-gray-200 text-right">
                      {val2 != null ? fmt(val2) : 'N/A'}
                    </span>
                    <span className={`text-[10px] font-mono font-semibold text-right ${ci.color}`}>
                      {ci.arrow ? `${ci.arrow} ${ci.text}` : ci.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ---- 5. COMPONENT SCORE COMPARISON — SIDE-BY-SIDE BARS ---- */}
          {risk1?.componentScores && risk2?.componentScores && (
            <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-brand-500/20 rounded-md p-1.5">
                  <svg className="w-4 h-4 text-brand-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-gray-200">Component Score Comparison</h3>
              </div>

              <div className="space-y-3">
                {Object.entries(COMPONENT_LABELS).map(([key, label]) => {
                  const s1 = risk1.componentScores[key] ?? 0;
                  const s2 = risk2.componentScores[key] ?? 0;
                  const diff = s1 - s2;
                  const improved = diff > 1;
                  const regressed = diff < -1;

                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className="w-16 text-xs text-gray-400 flex-shrink-0">{label}</span>

                      {/* V1 bar (indigo) */}
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 h-2.5 bg-surface-600 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-indigo-500"
                            style={{ width: `${Math.min(100, s1)}%` }}
                          />
                        </div>
                        <span className={`w-8 text-xs font-mono font-semibold text-right ${getScoreTextColor(s1)}`}>
                          {Math.round(s1)}
                        </span>
                      </div>

                      {/* Delta */}
                      <span className={`w-16 text-center text-xs font-mono font-semibold ${
                        improved ? 'text-emerald-400' : regressed ? 'text-red-400' : 'text-gray-500'
                      }`}>
                        {improved ? `+${Math.round(diff)}` : regressed ? `${Math.round(diff)}` : '='}
                      </span>

                      {/* V2 bar (emerald) */}
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 h-2.5 bg-surface-600 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500"
                            style={{ width: `${Math.min(100, s2)}%` }}
                          />
                        </div>
                        <span className={`w-8 text-xs font-mono font-semibold text-right ${getScoreTextColor(s2)}`}>
                          {Math.round(s2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-600/50">
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <span className="w-2.5 h-2.5 bg-indigo-500 rounded-sm" /> {v1Label}
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <span className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" /> {v2Label}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* No data state */}
      {!loading && comparison && !vData1 && !vData2 && (
        <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 text-center py-12 text-gray-500">
          No data found for selected versions.
        </div>
      )}

      {/* Empty state */}
      {!loading && !comparison && !v1 && !v2 && (
        <div className="bg-surface-700/40 rounded-xl border border-surface-600/50 text-center py-12 text-gray-500">
          Select two versions above to compare stability metrics.
        </div>
      )}
    </div>
  );
}
